import axios, {AxiosResponse} from 'axios';

import {$$} from './util/$$';
import {Breakpoint} from './Breakpoint';
import {Modal} from './modal';

const TRIGGER_CLASS: string = '.z-tooltip';
const DATA_ATTRIBUTE: string = 'data-glossary-id';

type TooltipConfig = {
    rootSelector: string;
    titleSelector: string;
    bodySelector: string;
};

type Data = {
    title: {
        rendered: string;
    };
    excerpt: {
        rendered: string;
    };
};

const CLASS_OPEN: string = 'open';

export class Tooltip {
    private static _instance: Tooltip | undefined;

    private _isOpen: boolean = false;
    private readonly _body: HTMLElement | null;
    private readonly _root: HTMLElement | null;
    private readonly _title: HTMLElement | null;

    private constructor(config: TooltipConfig) {
        this._root = document.querySelector(config.rootSelector);
        this._title = document.querySelector(config.titleSelector);
        this._body = document.querySelector(config.bodySelector);

        this.addEventHandlers();
    }

    private set anchor(anchor: HTMLElement) {
        if (this._root === null) {
            return;
        }

        const rect: ClientRect = anchor.getBoundingClientRect();
        const scrollTop: number = document.documentElement.scrollTop;
        const top: number = rect.top - 20;

        this._root.style.top = `${top + scrollTop}px`;
    }

    private set title(title: string) {
        if (this._title === null) {
            return;
        }

        this._title.innerText = title;
    }

    private set body(body: string) {
        if (this._body === null) {
            return;
        }

        // tslint:disable-next-line no-inner-html
        this._body.innerHTML = body;
    }

    // tslint:disable-next-line function-name
    public static config(config: TooltipConfig): void {
        this._instance = new Tooltip(config);
    }

    // tslint:disable-next-line function-name
    public static open(title: string, body: string, anchor: HTMLElement): void {
        if (this._instance === undefined) {
            throw new Error('Please configure the tooltip before usage');
        }

        const bp: string | undefined = Breakpoint.current();

        if (Breakpoint.smaller('md')) {
            Modal.open(title, body, 't-yellow--golden-poppy');

            return;
        }

        this._instance.anchor = anchor;
        this._instance.title = title;
        this._instance.body = body;
        this._instance.show();
    }

    // tslint:disable-next-line function-name
    public static close(): void {
        if (this._instance === undefined) {
            return;
        }

        this._instance.hide();
    }

    private addEventHandlers(): void {
        document.body.addEventListener('click', (): void => {
            if (this._isOpen) {
                this.hide();
            }
        });

        if (this._root !== null) {
            this._root.addEventListener('click', (event: Event): void => {
                event.stopPropagation();
            });
        }
    }

    private show(): void {
        if (this._root === null) {
            return;
        }

        this._root.classList.add(CLASS_OPEN);
        this._isOpen = true;
    }

    private hide(): void {
        if (this._root === null) {
            return;
        }

        this._root.classList.remove(CLASS_OPEN);
        this._isOpen = false;
    }
}

function showTooltip(event: Event): void {
    const element: HTMLElement = <HTMLElement>event.target;
    const id: string | null = element.getAttribute(DATA_ATTRIBUTE);

    if (id === null) {
        return;
    }

    axios
        .get(`/wp-json/wp/v2/glossary/${id}`)
        .then((response: AxiosResponse<Data>): void => {
            Tooltip.open(
                response.data.title.rendered,
                response.data.excerpt.rendered,
                element,
            );
        });
}

export function tooltip(): void {
    $$(TRIGGER_CLASS).forEach((element: HTMLElement): void => {
        element.addEventListener('click', showTooltip);
    });
}
